/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useSearchParams } from '@/lib/scopedParams';
import { api } from '../../api';
import type { Promotion, PromotionUsage } from '../../api/areas/commerce';
import { Badge, type Tone } from '../../components/Badge';
import { Callout, DataTable, FilterBar, Section, StatCard, StatGrid, type Column } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { KeyValue } from '../../components/KeyValue';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { EmptyState, Loading } from '../../components/States';
import { formatCount, formatDateTime } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { NewPromotionButton, PromotionActions } from '../operator/Pricing';
import {
  Amount,
  cellLink,
  clearParams,
  CustomerRef,
  FilterSelect,
  Label,
  OrderRef,
  promotionPath,
  QuoteRef,
  RefreshButton,
  ResetFilters,
  RichText,
  setParam,
  Status,
  useEnumLabel,
  When,
} from './shared';
import { Link } from 'react-router';

type PromoState = 'ACTIVE' | 'SCHEDULED' | 'ENDED' | 'EXHAUSTED';
const STATES: readonly PromoState[] = ['ACTIVE', 'SCHEDULED', 'EXHAUSTED', 'ENDED'];
const STATE_TONE: Record<PromoState, Tone> = { ACTIVE: 'ok', SCHEDULED: 'info', EXHAUSTED: 'warn', ENDED: 'neutral' };

function promoState(p: Promotion, now: string): PromoState {
  // A disabled code prices nothing any more: it has ended early.
  if (p.disabled_at) return 'ENDED';
  if (p.ends_at <= now) return 'ENDED';
  if (p.starts_at > now) return 'SCHEDULED';
  if (p.max_redemptions !== null && p.redemptions >= p.max_redemptions) return 'EXHAUSTED';
  return 'ACTIVE';
}

function discount(p: Promotion) {
  return p.discount_type === 'PERCENT' ? <span className="tabular">{p.value} %</span> : <Amount amount={p.value} currency={p.currency} />;
}

function AppliesTo({ p }: { p: Promotion }) {
  const { t } = useTranslation('commerce');
  return (
    <span className="whitespace-nowrap">
      {p.operation ? <Label group="operation" value={p.operation} /> : t('promotions.anyOperation')} · {p.tld ? <span className="font-medium">.{p.tld}</span> : t('promotions.allTlds')}
    </span>
  );
}

/** `/promotions`: the discount codes customers can use, and how much of each is left. */
export function PromotionsPage() {
  const { t } = useTranslation('commerce');
  const { t: to } = useTranslation('operator');
  const label = useEnumLabel('commerce');
  useDocumentTitle(t('promotions.title'));
  const [params, setParams] = useSearchParams();
  const state = params.get('state') ?? '';
  const result = useApi((signal) => api.listPromotions({ signal }), []);
  const now = new Date().toISOString();
  const all = result.status === 'success' ? result.data.items : [];
  const rows = all.filter((p) => !state || promoState(p, now) === state);
  const counts = Object.fromEntries(STATES.map((s) => [s, all.filter((p) => promoState(p, now) === s).length])) as Record<PromoState, number>;

  const columns: Column<Promotion>[] = [
    {
      key: 'code',
      header: t('promotions.col.code'),
      cell: (p) => (
        <span className="inline-flex items-center gap-2">
          <Link to={promotionPath(p.id)} className={`${cellLink} font-mono font-semibold`}>
            {p.code}
          </Link>
          {p.disabled_at ? <Badge tone="neutral">{to('promotions.disabled')}</Badge> : null}
        </span>
      ),
    },
    { key: 'discount', header: t('promotions.col.discount'), className: 'whitespace-nowrap', cell: (p) => discount(p) },
    { key: 'applies', header: t('promotions.col.appliesTo'), cell: (p) => <AppliesTo p={p} /> },
    { key: 'state', header: t('promotions.col.state'), cell: (p) => <Status group="promoState" tones={STATE_TONE} value={promoState(p, now)} /> },
    {
      key: 'window',
      header: t('promotions.col.window'),
      className: 'whitespace-nowrap text-xs',
      cell: (p) => (
        <>
          <div>{formatDateTime(p.starts_at)}</div>
          <div className="text-muted-foreground">→ {formatDateTime(p.ends_at)}</div>
        </>
      ),
    },
    {
      key: 'redemptions',
      header: t('promotions.col.redemptions'),
      numeric: true,
      cell: (p) => (
        <>
          {formatCount(p.redemptions)}
          {p.max_redemptions !== null ? <span className="text-muted-foreground"> / {formatCount(p.max_redemptions)}</span> : null}
        </>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('promotions.title')}
        crumbs={[{ label: t('crumb') }]}
        subtitle={<RichText k="promotions.subtitle" />}
        actions={
          <>
            <RefreshButton onClick={result.reload} />
            <NewPromotionButton onChanged={result.reload} />
          </>
        }
      />
      <div className="flex flex-col gap-6">
        {result.status === 'success' ? (
          <StatGrid>
            {STATES.map((s) => (
              <StatCard
                key={s}
                label={label('promoState', s)}
                value={formatCount(counts[s])}
                tone={counts[s] > 0 ? STATE_TONE[s] : 'neutral'}
                to={`/promotions?state=${s}`}
                linkLabel={t('promotions.filterBy', { state: label('promoState', s).toLowerCase() })}
              />
            ))}
          </StatGrid>
        ) : null}
        <div>
          <FilterBar actions={<ResetFilters show={Boolean(state)} onReset={() => setParams(clearParams(params, ['state']))} />}>
            <FilterSelect label={t('promotions.stateFilter')} value={state} options={STATES} group="promoState" onChange={(v) => setParams(setParam(params, 'state', v))} />
          </FilterBar>
          <Section flush>
            {result.status === 'loading' ? (
              <div className="px-5">
                <Loading rows={4} />
              </div>
            ) : null}
            {result.status === 'error' ? (
              <div className="px-5 pb-5">
                <DataError error={result.error} onRetry={result.reload} title={t('promotions.loadError')} />
              </div>
            ) : null}
            {result.status === 'success' ? <DataTable columns={columns} rows={rows} rowKey={(p) => p.id} empty={<EmptyState title={t('promotions.empty')} />} /> : null}
          </Section>
        </div>
      </div>
    </>
  );
}

/** `/promotions/:promotionId`: one code and the quotes that used it. */
export function PromotionDetailPage() {
  const { t } = useTranslation('commerce');
  const { t: to } = useTranslation('operator');
  const label = useEnumLabel('commerce');
  const { promotionId = '' } = useParams();
  useDocumentTitle(t('promotion.title'));
  const result = useApi((signal) => api.getPromotion(promotionId, { signal }), [promotionId]);
  const crumbs = [{ label: t('crumb') }, { label: t('promotions.title'), to: '/promotions' }];
  if (result.status === 'loading') return <Loading rows={4} />;
  if (result.status === 'error') {
    return (
      <>
        <PageHeader title={t('promotion.title')} crumbs={crumbs} />
        <DataError error={result.error} onRetry={result.reload} title={t('promotion.loadError')} />
      </>
    );
  }
  const { promotion: p, usage } = result.data;
  const state = promoState(p, new Date().toISOString());
  const left = p.max_redemptions === null ? null : Math.max(0, p.max_redemptions - p.redemptions);
  const usageColumns: Column<PromotionUsage>[] = [
    { key: 'domain', header: t('promotion.usageCol.domain'), cell: (u) => <QuoteRef id={u.quote_id} domain={u.domain_name} sub={`.${u.tld}`} /> },
    { key: 'customer', header: t('promotion.usageCol.customer'), cell: (u) => <CustomerRef id={u.customer_id} name={u.customer_name} /> },
    { key: 'discount', header: t('promotion.usageCol.discount'), numeric: true, cell: (u) => <Amount amount={u.discount} currency={u.currency} /> },
    { key: 'total', header: t('promotion.usageCol.total'), numeric: true, cell: (u) => <Amount amount={u.total} currency={u.currency} className="font-semibold" /> },
    { key: 'order', header: t('promotion.usageCol.order'), cell: (u) => (u.order_id ? <OrderRef id={u.order_id} domain={u.order_domain} count={u.order_item_count} at={u.order_placed_at} /> : <span className="text-muted-foreground">{t('promotion.notOrdered')}</span>) },
    { key: 'created', header: t('promotion.usageCol.created'), cell: (u) => <When at={u.created_at} /> },
  ];
  return (
    <>
      <PageHeader
        title={<span className="font-mono">{p.code}</span>}
        crumbs={[...crumbs, { label: p.code }]}
        subtitle={<AppliesTo p={p} />}
        actions={
          <>
            <RefreshButton onClick={result.reload} />
            <PromotionActions promotion={p} onChanged={result.reload} />
          </>
        }
      />
      <div className="flex flex-col gap-6">
        {p.disabled_at ? <Callout tone="warn">{to('promotions.disabledOn', { when: formatDateTime(p.disabled_at), reason: p.disabled_reason ?? '—' })}</Callout> : null}
        <StatGrid className="xl:grid-cols-3">
          <StatCard label={t('promotion.stat.state')} value={label('promoState', state)} tone={STATE_TONE[state]} />
          <StatCard label={t('promotion.stat.discount')} value={discount(p)} />
          <StatCard
            label={t('promotion.stat.redemptions')}
            value={p.max_redemptions === null ? formatCount(p.redemptions) : `${formatCount(p.redemptions)} / ${formatCount(p.max_redemptions)}`}
            hint={left === null ? t('promotion.field.noLimit') : t('promotion.stat.left', { count: left })}
            tone={left === 0 ? 'warn' : 'neutral'}
          />
        </StatGrid>
        <Section>
          <KeyValue
            columns={3}
            rows={[
              [t('promotion.field.code'), <code key="c" className="font-mono font-semibold">{p.code}</code>],
              [t('promotions.col.state'), <Status key="s" group="promoState" tones={STATE_TONE} value={state} />],
              [t('promotions.col.discount'), discount(p)],
              [t('promotions.col.appliesTo'), <AppliesTo key="a" p={p} />],
              [t('promotion.field.starts'), formatDateTime(p.starts_at)],
              [t('promotion.field.ends'), formatDateTime(p.ends_at)],
              [t('promotion.field.limit'), p.max_redemptions === null ? t('promotion.field.noLimit') : formatCount(p.max_redemptions)],
              [t('promotions.col.redemptions'), formatCount(p.redemptions)],
              [t('promotion.field.created'), formatDateTime(p.created_at)],
            ]}
          />
        </Section>
        <Section title={t('promotion.usage')} description={t('promotion.usageNote')} flush>
          <DataTable columns={usageColumns} rows={usage} rowKey={(u) => u.quote_id} empty={<EmptyState title={t('promotion.noUsage')} />} />
        </Section>
      </div>
    </>
  );
}
